// Importar axios con interceptor de autenticación configurado
import axios from '../auth/authInterceptor';

/**
 * Servicio para gestionar los datos de la base de datos de prospectos
 */
export const baseDatosService = {
  /**
   * Obtiene los datos de prospectos para la vista de base de datos
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.page - Número de página
   * @param {number} params.limit - Elementos por página
   * @param {string} params.search - Término de búsqueda
   * @param {string} params.fecha_desde - Fecha de inicio (YYYY-MM-DD)
   * @param {string} params.fecha_hasta - Fecha final (YYYY-MM-DD)
   * @param {string} params.temperatura - Filtro por temperatura
   * @param {string} params.orderBy - Campo para ordenar
   * @param {string} params.orderDirection - Dirección del ordenamiento (ASC/DESC)
   * @note El código de vendedor se obtiene automáticamente del token JWT en el backend
   * @returns {Promise<Object>} Respuesta con datos y paginación
   */
  async getBaseDatos(params = {}) {
    try {
      const requestParams = {
        page: params.page || 1,
        limit: params.limit || 50,
        search: params.search || undefined,
        fecha_desde: params.fecha_desde || undefined,
        fecha_hasta: params.fecha_hasta || undefined,
        estado_clasificacion: params.estado_clasificacion || undefined,
        orderBy: params.orderBy || 'fecha_creacion',
        orderDirection: params.orderDirection || 'DESC',
      };

      console.log('📡 [baseDatosService] Enviando al backend:', requestParams);

      const response = await axios.get('/api/prospectos/base-datos', {
        params: requestParams,
      });

      return response.data;
    } catch (error) {
      console.error('Error al obtener datos de base:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al cargar los datos de la base de datos'
      );
    }
  },


  async getDetalleLeadClasificacion(leadUuid) {
    try {
      console.log('[baseDatosService] Obteniendo detalle de lead:', leadUuid);

      const response = await axios.get(`/api/prospectos/detalle-lead/${leadUuid}`);

      console.log('[baseDatosService] Detalle obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al obtener detalle del lead:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al obtener detalle del lead'
      );
    }
  },

  /**
   * Formatea los datos recibidos del backend para el frontend
   * @param {Array} data - Datos del backend
   * @returns {Array} Datos formateados para el frontend
   */
  formatDataForFrontend(data) {
    return data.map(item => ({
      id: item.id_prospecto,
      nombre: item.nombre || 'Base de datos principal',
      tipoRegistro: this.formatTipoRegistro(item.tipoRegistro),
      etiqueta: item.etiqueta || 'Prospectos',
      idLead: item.idLead,
      leadUuid: item.leadUuid,
      fechaCreacion: this.formatDate(item.fecha_creacion),
      fechaCita: this.formatDate(item.fechaCita),
      fechaDerivacion: this.formatDate(item.fechaDerivacion),
      tiempoDerivacion: this.formatTiempoDerivacion(item.tiempoDerivacion),
      origen: item.origen || 'Sistema',
      contactado: item.contactado,
      nombrePersona: item.nombrePersona,
      apellido: item.apellido,
      dni: item.dni,
      celular: item.celular,
      correo: item.correo,
      // Clasificación (nuevo)
      estadoClasificacion: item.estadoClasificacion || 'pendiente',
      totalClasificaciones: item.totalClasificaciones || 0,
      totalCitas: item.totalCitas || 0,
      // Legacy
      temperatura: this.formatTemperatura(item.temperatura),
      marca: item.marca || 'N/A',
      idProducto: item.idProducto,
      productos: item.productos,
      tienda: item.tienda,
      departamento: item.departamento,
      distrito: item.distrito,
    }));
  },

  /**
   * Formatea una fecha para mostrar en el frontend
   * @param {string} dateString - Fecha en formato ISO
   * @returns {string} Fecha formateada (DD/MM/YY)
   */
  formatDate(dateString) {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'N/A';
    }
  },

  /**
   * Formatea el tiempo de derivación
   * @param {number} hours - Horas de diferencia
   * @returns {string} Tiempo formateado
   */
  formatTiempoDerivacion(hours) {
    if (!hours || hours < 0) return '00:00';

    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    return `${wholeHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  },

  /**
   * Formatea la temperatura del lead
   * @param {string} temperatura - Temperatura del backend
   * @returns {string} Temperatura formateada
   */
  formatTemperatura(temperatura) {
    if (!temperatura) return 'No contactado';

    const temperaturaMap = {
      'alto': 'Caliente',
      'medio': 'Tibio',
      'bajo': 'Frío',
      'caliente': 'Caliente',
      'tibio': 'Tibio',
      'frio': 'Frío',
      'frío': 'Frío'
    };

    return temperaturaMap[temperatura.toLowerCase()] || temperatura;
  },

  /**
   * Formatea el tipo de registro
   * @param {string} tipoRegistro - Tipo de registro del backend ('automatico' o 'manual')
   * @returns {string} Tipo de registro formateado con primera letra mayúscula
   */
  formatTipoRegistro(tipoRegistro) {
    if (!tipoRegistro) return 'N/A';

    if (tipoRegistro === 'automatico') {
      return 'Automático';
    } else if (tipoRegistro === 'manual') {
      return 'Manual';
    } else {
      return 'N/A';
    }
  },

  /**
   * Convierte los filtros del frontend al formato del backend
   * @param {Object} filters - Filtros del frontend
   * @returns {Object} Filtros formateados para el backend
   */
  formatFiltersForBackend(filters) {
    const backendFilters = {};

    if (filters.dateRange?.start) {
      backendFilters.fecha_desde = this.formatDateForBackend(filters.dateRange.start);
    }

    if (filters.dateRange?.end) {
      backendFilters.fecha_hasta = this.formatDateForBackend(filters.dateRange.end);
    }

    // Nuevo: filtro por clasificación
    if (filters.clasificacion?.length > 0) {
      backendFilters.estado_clasificacion = filters.clasificacion[0];
    }

    return backendFilters;
  },

  /**
   * Formatea una fecha para enviar al backend
   * @param {Date} date - Objeto Date
   * @returns {string} Fecha en formato YYYY-MM-DD
   */
  formatDateForBackend(date) {
    if (!date) return null;

    try {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error al formatear fecha para backend:', error);
      return null;
    }
  },

  /**
   * Elimina registros por sus IDs de prospecto
   * @param {Array} prospectIds - Array de id_prospecto a eliminar
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async deleteRecords(prospectIds) {
    try {
      const response = await axios.delete('/api/prospectos/base-datos/bulk-delete', {
        data: {
          prospect_ids: prospectIds // Usar prospect_ids para ser más específico
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al eliminar registros:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al eliminar los registros'
      );
    }
  },

  /**
   * Obtiene todos los datos para exportar a Excel
   * @param {Object} filters - Filtros aplicados
   * @param {string} search - Término de búsqueda
   * @returns {Promise<Object>} Respuesta con todos los datos
   * @note El código de vendedor se obtiene automáticamente del token JWT en el backend
   */
  async getAllDataForExport(filters = {}, search = '') {
    try {
      const backendFilters = this.formatFiltersForBackend(filters);

      // Intentar primero el endpoint específico de exportación
      try {
        const response = await axios.get('/api/prospectos/base-datos/export', {
          params: {
            search: search || undefined,
            // 🔑 CÓDIGO DE VENDEDOR SE OBTIENE AUTOMÁTICAMENTE DEL TOKEN JWT EN EL BACKEND
            ...backendFilters,
          },
        });
        return response.data;
      } catch (exportError) {
        console.log('Endpoint de exportación no disponible, usando endpoint principal con límite alto');

        // Fallback: usar el endpoint principal con un límite muy alto
        const response = await axios.get('/api/prospectos/base-datos', {
          params: {
            page: 1,
            limit: 10000, // Límite alto para obtener todos los datos
            search: search || undefined,
            // 🔑 CÓDIGO DE VENDEDOR SE OBTIENE AUTOMÁTICAMENTE DEL TOKEN JWT EN EL BACKEND
            ...backendFilters,
            orderBy: 'fecha_creacion',
            orderDirection: 'DESC',
          },
        });

        // Asegurar que la respuesta tenga la estructura esperada
        if (response.data && response.data.success) {
          return {
            success: true,
            data: response.data.data.data || response.data.data || [],
            message: 'Datos obtenidos correctamente'
          };
        } else {
          return {
            success: true,
            data: response.data.data || [],
            message: 'Datos obtenidos correctamente'
          };
        }
      }
    } catch (error) {
      console.error('Error al obtener datos para exportar:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al obtener los datos para exportar'
      );
    }
  },

  /**
   * Obtiene estadísticas de prospectos
   * @param {Object} filtros - Filtros para las estadísticas
   * @returns {Promise<Object>} - Estadísticas de prospectos
   */
  async getEstadisticasProspectos(filtros = {}) {
    try {
      console.log('📊 [baseDatosService] Obteniendo estadísticas de prospectos...', filtros);

      const response = await axios.get('/api/prospectos/estadisticas', {
        params: {
          fecha_desde: filtros.fecha_desde || undefined,
          fecha_hasta: filtros.fecha_hasta || undefined,
          temperatura: filtros.temperatura || undefined,
          origen: filtros.origen || undefined
        },
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Estadísticas obtenidas:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error obteniendo estadísticas:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al obtener estadísticas de prospectos'
      );
    }
  },

  /**
   * Obtiene top prospectos por score
   * @param {Object} params - Parámetros de consulta
   * @returns {Promise<Object>} - Top prospectos
   */
  async getTopProspectos(params = {}) {
    try {
      console.log('🏆 [baseDatosService] Obteniendo top prospectos...', params);

      const response = await axios.get('/api/prospectos/top', {
        params: {
          limit: params.limit || 10,
          fecha_desde: params.fecha_desde || undefined,
          fecha_hasta: params.fecha_hasta || undefined,
          temperatura: params.temperatura || undefined
        },
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Top prospectos obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error obteniendo top prospectos:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al obtener top prospectos'
      );
    }
  },

  /**
   * Busca prospectos con criterios específicos
   * @param {Object} criterios - Criterios de búsqueda
   * @returns {Promise<Object>} - Resultados de búsqueda
   */
  async buscarProspectos(criterios) {
    try {
      console.log('🔍 [baseDatosService] Buscando prospectos...', criterios);

      const response = await axios.post('/api/prospectos/buscar', criterios, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Búsqueda completada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error en búsqueda:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al buscar prospectos'
      );
    }
  },

  /**
   * Verifica si existe un prospecto
   * @param {Object} datos - Datos para verificar (dni, telefono, email)
   * @returns {Promise<Object>} - Resultado de verificación
   */
  async verificarProspecto(datos) {
    try {
      console.log('🔍 [baseDatosService] Verificando prospecto...', datos);

      const response = await axios.get('/api/prospectos/verificar', {
        params: datos,
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Verificación completada:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error verificando prospecto:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al verificar prospecto'
      );
    }
  },

  /**
   * Exporta prospectos a Excel
   * @param {Object} filtros - Filtros para la exportación
   * @returns {Promise<Blob>} - Archivo Excel
   */
  async exportarProspectosExcel(filtros = {}) {
    try {
      console.log('📥 [baseDatosService] Exportando prospectos a Excel...', filtros);

      const response = await axios.get('/api/prospectos/exportar-excel', {
        params: {
          search: filtros.search || undefined,
          fecha_desde: filtros.fecha_desde || undefined,
          fecha_hasta: filtros.fecha_hasta || undefined,
          temperatura: filtros.temperatura || undefined,
          origen: filtros.origen || undefined
        },
        responseType: 'blob',
        timeout: 60000
      });

      console.log('✅ [baseDatosService] Excel exportado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error exportando Excel:', error);
      throw new Error(
        error.response?.data?.message ||
        'Error al exportar prospectos a Excel'
      );
    }
  },

  /**
   * Sube archivo Excel de contactos
   * @param {File} file - Archivo Excel
   * @param {Object} metadata - Metadatos adicionales
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async uploadExcelContactos(file, metadata = {}) {
    try {
      console.log('📤 [baseDatosService] Subiendo Excel de contactos...', metadata);

      const formData = new FormData();
      formData.append('excel', file);

      // Agregar metadatos
      Object.keys(metadata).forEach(key => {
        if (metadata[key] !== null && metadata[key] !== undefined) {
          formData.append(key, metadata[key]);
        }
      });

      const response = await axios.post('/api/prospectos/upload-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000, // 2 minutos
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`📊 [baseDatosService] Progreso: ${percentCompleted}%`);
        }
      });

      console.log('✅ [baseDatosService] Excel subido exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error subiendo Excel:', error);

      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            throw new Error(data.message || 'Archivo Excel inválido');
          case 413:
            throw new Error('El archivo es demasiado grande');
          case 415:
            throw new Error('Formato de archivo no soportado');
          case 422:
            throw new Error(data.message || 'Error de validación en el archivo');
          default:
            throw new Error(data.message || 'Error desconocido al subir archivo');
        }
      }

      throw error;
    }
  },

  /**
   * Obtiene un prospecto por código
   * @param {string} codigoPersona - Código de la persona
   * @returns {Promise<Object>} - Datos del prospecto
   */
  async getProspectoPorCodigo(codigoPersona) {
    try {
      console.log(`👤 [baseDatosService] Obteniendo prospecto: ${codigoPersona}`);

      const response = await axios.get(`/api/prospectos/codigo/${codigoPersona}`, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Prospecto obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error obteniendo prospecto:', error);

      if (error.response?.status === 404) {
        throw new Error('Prospecto no encontrado');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al obtener el prospecto'
      );
    }
  },

  /**
   * Actualiza el score de un prospecto
   * @param {string} codigoPersona - Código de la persona
   * @param {number} score - Nuevo score
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async actualizarScore(codigoPersona, score) {
    try {
      console.log(`📊 [baseDatosService] Actualizando score: ${codigoPersona} -> ${score}`);

      const response = await axios.put(`/api/prospectos/codigo/${codigoPersona}/score`, {
        score: score
      }, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Score actualizado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error actualizando score:', error);

      if (error.response?.status === 404) {
        throw new Error('Prospecto no encontrado');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al actualizar el score'
      );
    }
  },

  /**
   * Bloquea o desbloquea un prospecto
   * @param {string} codigoPersona - Código de la persona
   * @param {boolean} bloqueado - Estado de bloqueo
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async actualizarBloqueo(codigoPersona, bloqueado) {
    try {
      console.log(`🔒 [baseDatosService] Actualizando bloqueo: ${codigoPersona} -> ${bloqueado}`);

      const response = await axios.put(`/api/prospectos/codigo/${codigoPersona}/bloqueo`, {
        bloqueado: bloqueado
      }, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Bloqueo actualizado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error actualizando bloqueo:', error);

      if (error.response?.status === 404) {
        throw new Error('Prospecto no encontrado');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al actualizar el bloqueo'
      );
    }
  },

  /**
   * Obtiene un prospecto por ID
   * @param {string|number} id - ID del prospecto
   * @returns {Promise<Object>} - Datos del prospecto
   */
  async getProspectoPorId(id) {
    try {
      console.log(`👤 [baseDatosService] Obteniendo prospecto ID: ${id}`);

      const response = await axios.get(`/api/prospectos/${id}`, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Prospecto obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error obteniendo prospecto:', error);

      if (error.response?.status === 404) {
        throw new Error('Prospecto no encontrado');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al obtener el prospecto'
      );
    }
  },

  /**
   * Crea un nuevo prospecto
   * @param {Object} prospectoData - Datos del prospecto
   * @returns {Promise<Object>} - Prospecto creado
   */
  async crearProspecto(prospectoData) {
    try {
      console.log('➕ [baseDatosService] Creando prospecto...', prospectoData);

      const response = await axios.post('/api/prospectos/', prospectoData, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Prospecto creado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error creando prospecto:', error);

      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Datos del prospecto inválidos');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al crear el prospecto'
      );
    }
  },

  /**
   * Actualiza un prospecto existente
   * @param {string|number} id - ID del prospecto
   * @param {Object} prospectoData - Datos actualizados
   * @returns {Promise<Object>} - Prospecto actualizado
   */
  async actualizarProspecto(id, prospectoData) {
    try {
      console.log(`✏️ [baseDatosService] Actualizando prospecto ID: ${id}`);

      const response = await axios.put(`/api/prospectos/${id}`, prospectoData, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Prospecto actualizado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error actualizando prospecto:', error);

      if (error.response?.status === 404) {
        throw new Error('Prospecto no encontrado');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Datos del prospecto inválidos');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al actualizar el prospecto'
      );
    }
  },

  /**
   * Elimina un prospecto
   * @param {string|number} id - ID del prospecto
   * @returns {Promise<Object>} - Respuesta de eliminación
   */
  async eliminarProspecto(id) {
    try {
      console.log(`🗑️ [baseDatosService] Eliminando prospecto ID: ${id}`);

      const response = await axios.delete(`/api/prospectos/${id}`, {
        timeout: 10000
      });

      console.log('✅ [baseDatosService] Prospecto eliminado:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [baseDatosService] Error eliminando prospecto:', error);

      if (error.response?.status === 404) {
        throw new Error('Prospecto no encontrado');
      }

      throw new Error(
        error.response?.data?.message ||
        'Error al eliminar el prospecto'
      );
    }
  }
};

export default baseDatosService;